// AuthFormInput.tsx - Google Modern form input component for auth forms
// Genesis v3.0 standard: Micro-module under 100 lines

import type { LucideIcon } from 'lucide-react';
import React from 'react';
import type { UseFormRegisterReturn } from 'react-hook-form';

interface AuthFormInputProps {
  type: 'text' | 'email' | 'password';
  label: string;
  name: string;
  register: UseFormRegisterReturn;
  placeholder: string;
  icon: LucideIcon;
  disabled?: boolean;
  required?: boolean;
  minLength?: number;
  autoComplete?: string;
  error?: string | null;
  showForgotPassword?: boolean;
  onForgotPassword?: () => void;
}

const AuthFormInput: React.FC<AuthFormInputProps> = ({
  type,
  label,
  name,
  register,
  placeholder,
  icon: Icon,
  disabled = false,
  required = true,
  minLength,
  autoComplete,
  error,
  showForgotPassword = false,
  onForgotPassword,
}) => {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between ml-1">
        <label htmlFor={name} className="text-sm font-semibold text-[var(--color-content-secondary)]">
          {label}
        </label>
        {showForgotPassword && onForgotPassword && (
          <button
            type="button"
            onClick={onForgotPassword}
            className="text-xs font-bold text-[var(--color-primary-base)] hover:underline"
          >
            Forgot password?
          </button>
        )}
      </div>
      <div className="relative group">
        <Icon
          className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--color-content-tertiary)] group-focus-within:text-[var(--color-primary-base)] transition-colors"
          size={20}
        />
        <input
          id={name}
          type={type}
          {...register}
          placeholder={placeholder}
          required={required}
          minLength={minLength}
          disabled={disabled}
          autoComplete={autoComplete}
          className={`w-full h-14 pl-12 pr-4 theme-input border rounded-2xl outline-none focus:ring-1 transition-all font-medium text-[var(--color-input-text)] ${error
            ? 'border-[var(--color-danger-base)]/40 focus:border-[var(--color-danger-base)] focus:ring-[var(--color-danger-base)]'
            : 'border-[var(--color-input-border)] focus:border-[var(--color-primary-base)] focus:ring-[var(--color-primary-base)]'
            }`}
        />
      </div>
      {error && <p className="text-xs text-[var(--color-danger-base)] ml-1">{error}</p>}
      {minLength && !error && (
        <p className="text-xs text-[var(--color-content-tertiary)] ml-1">Min. {minLength} characters</p>
      )}
    </div>
  );
};

export default AuthFormInput;

